import type { Patient } from '@medplum/fhirtypes'
import { DOSESPOT_DEFAULT_PHARMACY_EXT } from '@/model/middleware-types'

/**
 * The stored default-pharmacy snapshot as the card renders it (PD-1386). Read from
 * the Patient extension only — the card never calls DoseSpot to display, so a
 * DoseSpot outage can never blank it. Shape mirrors the middleware writer in
 * packages/middleware/src/dosespot/utils/default-pharmacy.ts.
 */
export type StoredDefaultPharmacy = {
  pharmacyId: string
  storeName: string
  address1: string
  address2?: string
  city: string
  state: string
  zip: string
  phone: string
  fax?: string
  /** Joined display string, e.g. "Retail, TwentyFourHourPharmacy" — absent when DoseSpot said nothing. */
  specialties?: string
  /** ISO instant stamped by middleware at write time (PD-1504) — absent on pre-stamp defaults. */
  updatedAt?: string
}

export function readDefaultPharmacy(patient: Patient): StoredDefaultPharmacy | undefined {
  const extension = patient.extension?.find((e) => e.url === DOSESPOT_DEFAULT_PHARMACY_EXT)
  if (!extension?.extension) return undefined

  const value = (url: string) => {
    const found = extension.extension?.find((sub) => sub.url === url)?.valueString
    return found || undefined
  }

  const pharmacyId = value('pharmacyId')
  if (!pharmacyId) return undefined

  return {
    pharmacyId,
    storeName: value('storeName') ?? '',
    address1: value('address1') ?? '',
    address2: value('address2'),
    city: value('city') ?? '',
    state: value('state') ?? '',
    zip: value('zip') ?? '',
    phone: value('phone') ?? '',
    fax: value('fax'),
    specialties: value('specialties'),
    updatedAt: value('updatedAt'),
  }
}

/** One line for the card's Address row: "360 Park Ave S, Suite 2, New York, NY 10010". */
export function formatPharmacyAddress(pharmacy: {
  address1: string
  address2?: string
  city: string
  state: string
  zip: string
}): string {
  const cityLine = [pharmacy.city, [pharmacy.state, pharmacy.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
  return [pharmacy.address1, pharmacy.address2, cityLine].filter(Boolean).join(', ')
}
